import { useEffect } from "react";
import { getCommands, isPaletteOpen, setPaletteOpen } from "@/shared/lib/commands";
import { matchShortcut } from "@/shared/lib/shortcut";

/**
 * Attaches the single global keydown listener. ⌘K toggles the palette;
 * registered commands fire when matched (but not while the user is typing).
 * Two binding kinds: `shortcut` combos (`"mod+shift+o"`) and leader `chord`s
 * (`"g i"` = press g, then i) — chords sidestep browser-reserved combos like
 * ⌘⇧I (DevTools). Mount once, in the app shell.
 */
export function useGlobalShortcuts(): void {
  useEffect(() => {
    // Pending leader key (e.g. "g") and its expiry timer. A chord completes when
    // the next non-modifier key arrives within the window.
    let leader: string | null = null;
    let leaderTimer: ReturnType<typeof setTimeout> | undefined;
    const clearLeader = (): void => {
      leader = null;
      if (leaderTimer) clearTimeout(leaderTimer);
    };

    function onKeyDown(event: KeyboardEvent): void {
      if (matchShortcut(event, "mod+k")) {
        clearLeader();
        event.preventDefault();
        setPaletteOpen(!isPaletteOpen());
        return;
      }

      // A few commands (e.g. "new chat") must fire even while the composer is
      // focused — otherwise ⌘⇧O silently no-ops right when the user needs it.
      for (const command of getCommands()) {
        if (command.runInInput && command.shortcut && matchShortcut(event, command.shortcut)) {
          clearLeader();
          event.preventDefault();
          command.perform();
          return;
        }
      }

      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (typing) {
        clearLeader();
        return;
      }

      const key = event.key.toLowerCase();
      const hasMod = event.metaKey || event.ctrlKey || event.altKey;

      // Second press of a pending chord (e.g. g then i).
      if (leader && !hasMod && key.length === 1) {
        const chord = `${leader} ${key}`;
        clearLeader();
        for (const command of getCommands()) {
          if (command.chord === chord) {
            event.preventDefault();
            command.perform();
            return;
          }
        }
        return; // a leader followed by an unbound key is a no-op
      }
      clearLeader();

      for (const command of getCommands()) {
        if (command.shortcut && matchShortcut(event, command.shortcut)) {
          event.preventDefault();
          command.perform();
          return;
        }
      }

      // Begin a chord when a bare key opens some command's leader (e.g. "g").
      if (!hasMod && !event.shiftKey && key.length === 1) {
        const isLeader = getCommands().some((c) => c.chord?.startsWith(`${key} `));
        if (isLeader) {
          leader = key;
          leaderTimer = setTimeout(() => {
            leader = null;
          }, 1200);
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (leaderTimer) clearTimeout(leaderTimer);
    };
  }, []);
}
